import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Dropdown, Popup, Icon, Loader } from 'semantic-ui-react';
import { Bar, Pie, Line } from 'react-chartjs-2';
import { iRootState, iRootDispatch } from '../../store';
import {
  getIntegrationEfficiencyData,
  getIntegrationStatusData,
  getIntegrationTrendData,
  getIntegrationsByProduct,
  IntegrationEfficiencyData,
} from '../../services/dashboard';
import { FormatDateYMD } from '../../services/date';
import {
  Container,
  HeaderSection,
  Title,
  SubTitle,
  FilterGrid,
  FilterColumn,
  Label,
  ButtonGroup,
  PrimaryButton,
  SecondaryButton,
  ChartGrid,
  ChartCard,
  ChartTitle
} from './styles';
import { dangerNotification, successNotification } from '../../services/notification';

const periodOptions = [
  { key: 'last7days', value: 'last7days', text: 'Últimos 7 dias' },
  { key: 'last30days', value: 'last30days', text: 'Últimos 30 dias' },
  { key: 'last60days', value: 'last60days', text: 'Últimos 60 dias' },
  { key: 'last90days', value: 'last90days', text: 'Últimos 90 dias' }
];

const mapState = (state: iRootState) => {
  return {
    user: state.user,
    availableBrokersAndEstates: state.availableBrokersAndEstates
  };
};

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateCurrentPage: (state: any) => dispatch.currentPage.updateCurrentPage(state)
});

const IntegrationsDashboard = (props: any) => {
  const [selectedPeriod, setSelectedPeriod] = useState<string>('last7days');
  const [selectedEstate, setSelectedEstate] = useState<string>('');
  const [selectedFranchise, setSelectedFranchise] = useState<string>('all');

  const [loading, setLoading] = useState<boolean>(false);
  const [metricsData, setMetricsData] = useState<IntegrationEfficiencyData>({
    totalRecords: 0,
    fullyIntegrated: 0,
    fullyManual: 0,
    fallback: 0,
    percentages: {
      fullyIntegrated: '0.0',
      fullyManual: '0.0',
      fallback: '0.0'
    }
  });
  const [chartData, setChartData] = useState<{
    statusDistribution: { labels: string[]; data: number[] };
    productData: { labels: string[]; datasets: any[] };
    trendData: { labels: string[]; datasets: any[] };
  }>({
    statusDistribution: { labels: [], data: [] },
    productData: { labels: [], datasets: [] },
    trendData: { labels: [], datasets: [] }
  });

  // Função para calcular as datas baseadas no período selecionado
  const getDateRange = () => {
    const endDate = new Date();
    const startDate = new Date();

    switch (selectedPeriod) {
      case 'last7days':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case 'last30days':
        startDate.setDate(endDate.getDate() - 30);
        break;
      case 'last60days':
        startDate.setDate(endDate.getDate() - 60);
        break;
      case 'last90days':
        startDate.setDate(endDate.getDate() - 90);
        break;
      default:
        startDate.setDate(endDate.getDate() - 7);
    }

    return {
      startDate: FormatDateYMD(startDate),
      endDate: FormatDateYMD(endDate)
    };
  };

  // Função para buscar dados do backend
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange();

      const params = {
        estateId: selectedEstate || undefined,
        franchiseId: selectedFranchise !== 'all' ? selectedFranchise : undefined,
        startDate,
        endDate
      };

      // Buscar todos os dados em paralelo
      const [efficiencyData, statusData, trendData, productData] = await Promise.all([
        getIntegrationEfficiencyData(params),
        getIntegrationStatusData(params),
        getIntegrationTrendData(params),
        getIntegrationsByProduct(params)
      ]);

      setMetricsData(efficiencyData);

      setChartData({
        statusDistribution: statusData.statusDistribution,
        productData: statusData.productData,
        trendData: trendData
      });
    } catch (error) {
      console.error('Erro ao buscar dados do dashboard:', error);
      dangerNotification('Erro', 'Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Buscar dados ao montar o componente e quando filtros mudarem
  useEffect(() => {
    fetchDashboardData();
  }, [selectedPeriod, selectedEstate, selectedFranchise]);

  const colors = {
    integrated: '#10B981',
    manual: '#F59E0B',
    fallback: '#EF4444',
    neutral: '#6B7280'
  };

  // Configurações dos gráficos
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const
      }
    },
    scales: {
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            precision: 0
          }
        }
      ]
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'right' as const
      }
    }
  };

  // Dados para gráfico de pizza (distribuição de status)
  const statusDistributionData = {
    labels: chartData.statusDistribution.labels,
    datasets: [
      {
        data: chartData.statusDistribution.data,
        backgroundColor: [colors.integrated, colors.manual, colors.fallback],
        borderColor: [colors.integrated, colors.manual, colors.fallback],
        borderWidth: 2
      }
    ]
  };

  const estateOptions = useMemo(() => {
    return (
      props.availableBrokersAndEstates.estates?.map((estate: any) => ({
        key: estate.value,
        value: estate.value,
        text: estate.text
      })) || []
    );
  }, [props.availableBrokersAndEstates.estates]);

  const franchiseOptions = props.availableBrokersAndEstates.franchises;

  const handleApplyFilters = () => {
    fetchDashboardData();
    successNotification('Sucesso', 'Filtros aplicados com sucesso!');
  };

  const handleClearFilters = () => {
    setSelectedPeriod('last7days');
    setSelectedEstate('');
    setSelectedFranchise('all');
    successNotification('Sucesso', 'Filtros limpos com sucesso!');
  };

  return (
    <Container>
      <HeaderSection>
        <Title>Dashboard de Eficiência das Integrações</Title>
        <SubTitle>
          Monitore a performance das integrações e identifique casos de fallback
          <br />
          <small style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
            Integrações ativas: Pottencial (PF), Porto Essencial (PF Residencial), Seguro
            Incêndio (Todas)
          </small>
        </SubTitle>
      </HeaderSection>

      <FilterGrid
        style={{
          gridTemplateColumns: '1fr 1fr 1fr auto',
          gap: '1rem',
          alignItems: 'end'
        }}
      >
        <FilterColumn>
          <Label>Período</Label>
          <Dropdown
            fluid
            selection
            placeholder="Selecionar período..."
            options={periodOptions}
            value={selectedPeriod}
            onChange={(e, { value }) => setSelectedPeriod(value as string)}
          />
        </FilterColumn>

        <FilterColumn>
          <Label>Imobiliária</Label>
          <Dropdown
            fluid
            selection
            search
            clearable
            placeholder="Selecionar imobiliária..."
            options={estateOptions}
            value={selectedEstate}
            onChange={(e, { value }) => setSelectedEstate(value as string)}
          />
        </FilterColumn>

        <FilterColumn>
          <Label>Franquia</Label>
          <Dropdown
            fluid
            selection
            clearable
            placeholder="Selecionar franquia..."
            options={franchiseOptions}
            value={selectedFranchise}
            onChange={(e, { value }) => setSelectedFranchise(value as string)}
          />
        </FilterColumn>

        <FilterColumn>
          <ButtonGroup style={{ marginTop: '0.5rem' }}>
            <PrimaryButton onClick={handleApplyFilters} disabled={loading}>
              {loading ? 'Carregando...' : 'Aplicar'}
            </PrimaryButton>
            <SecondaryButton onClick={handleClearFilters} disabled={loading}>
              Limpar
            </SecondaryButton>
          </ButtonGroup>
        </FilterColumn>
      </FilterGrid>

      {loading ? (
        <div style={{ textAlign: 'center', margin: '2rem 0' }}>
          <Loader active inline="centered" />
          <p>Carregando dados do dashboard...</p>
        </div>
      ) : (
        <>
          <ChartGrid
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              marginTop: '2rem'
            }}
          >
            <ChartCard>
              <ChartTitle
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                Total de Registros Processados
                <Popup
                  trigger={
                    <Icon
                      name="info circle"
                      style={{ color: '#6B7280', cursor: 'help' }}
                    />
                  }
                  content="Soma de todos os registros processados no período: Incêndio (Cotações e emissões) + Fiança (Emitidas)+ Capitalização (Emitidas) (integrados + manuais + fallback)"
                  position="top center"
                  size="small"
                />
              </ChartTitle>
              <div
                style={{
                  fontSize: '2.5rem',
                  fontWeight: 'bold',
                  color: '#374151',
                  textAlign: 'center',
                  marginTop: '1rem'
                }}
              >
                {metricsData.totalRecords.toLocaleString()}
              </div>
              <div
                style={{
                  fontSize: '0.875rem',
                  color: '#6B7280',
                  textAlign: 'center',
                  marginTop: '0.5rem'
                }}
              >
                {selectedPeriod === 'last7days'
                  ? 'Últimos 7 dias'
                  : selectedPeriod === 'last30days'
                  ? 'Últimos 30 dias'
                  : selectedPeriod === 'last60days'
                  ? 'Últimos 60 dias'
                  : selectedPeriod === 'last90days'
                  ? 'Últimos 90 dias'
                  : 'Período selecionado'}
              </div>
            </ChartCard>

            <ChartCard>
              <ChartTitle
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                Registros Totalmente Integrados
                <Popup
                  trigger={
                    <Icon
                      name="info circle"
                      style={{ color: '#6B7280', cursor: 'help' }}
                    />
                  }
                  content="Registros processados automaticamente sem intervenção manual: Seguro Incêndio (todas seguradoras) + Fiança (Pottencial PF / Porto Essencial PF Residencial)"
                  position="top center"
                  size="small"
                />
              </ChartTitle>
              <div
                style={{
                  fontSize: '2.5rem',
                  fontWeight: 'bold',
                  color: '#10B981',
                  textAlign: 'center',
                  marginTop: '1rem'
                }}
              >
                {metricsData.fullyIntegrated.toLocaleString()}
              </div>
              <div
                style={{
                  fontSize: '0.875rem',
                  color: '#6B7280',
                  textAlign: 'center',
                  marginTop: '0.5rem'
                }}
              >
                ({metricsData.percentages.fullyIntegrated}%) - Incêndio + Fiança integrada
              </div>
            </ChartCard>

            <ChartCard>
              <ChartTitle
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                Registros Totalmente Manuais
                <Popup
                  trigger={
                    <Icon
                      name="info circle"
                      style={{ color: '#6B7280', cursor: 'help' }}
                    />
                  }
                  content="Produtos sem integração + operações não suportadas: Capitalização emitida + Fiança de outras seguradoras + Fiança (Demais seguradoras, Porto PJ ou comercial, Pottencial PJ)"
                  position="top center"
                  size="small"
                />
              </ChartTitle>
              <div
                style={{
                  fontSize: '2.5rem',
                  fontWeight: 'bold',
                  color: '#F59E0B',
                  textAlign: 'center',
                  marginTop: '1rem'
                }}
              >
                {metricsData.fullyManual.toLocaleString()}
              </div>
              <div
                style={{
                  fontSize: '0.875rem',
                  color: '#6B7280',
                  textAlign: 'center',
                  marginTop: '0.5rem'
                }}
              >
                ({metricsData.percentages.fullyManual}%) - Capitalização emitida + Fiança
                emitida
              </div>
            </ChartCard>

            <ChartCard>
              <ChartTitle
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                Integração com Fallback
                <Popup
                  trigger={
                    <Icon
                      name="info circle"
                      style={{ color: '#6B7280', cursor: 'help' }}
                    />
                  }
                  content="Casos onde deveríamos processar automaticamente (Pottencial PF ou Porto Essencial PF Residencial), mas houve intervenção manual na análise ou confirmação"
                  position="top center"
                  size="small"
                />
              </ChartTitle>
              <div
                style={{
                  fontSize: '2.5rem',
                  fontWeight: 'bold',
                  color: '#EF4444',
                  textAlign: 'center',
                  marginTop: '1rem'
                }}
              >
                {metricsData.fallback.toLocaleString()}
              </div>
              <div
                style={{
                  fontSize: '0.875rem',
                  color: '#6B7280',
                  textAlign: 'center',
                  marginTop: '0.5rem'
                }}
              >
                ({metricsData.percentages.fallback}%) - Falhas na integração
              </div>
            </ChartCard>
          </ChartGrid>

          {/* Seção de Gráficos de Análise */}
          <ChartGrid style={{ marginTop: '2rem' }}>
            <ChartCard>
              <ChartTitle
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                Distribuição por Status de Processamento
                <Popup
                  trigger={
                    <Icon
                      name="info circle"
                      style={{ color: '#6B7280', cursor: 'help' }}
                    />
                  }
                  content="Gráfico de pizza mostrando a proporção entre registros integrados, manuais e com fallback no período selecionado"
                  position="top center"
                  size="small"
                />
              </ChartTitle>
              <div style={{ height: '300px', padding: '1rem' }}>
                {chartData.statusDistribution.data.length > 0 ? (
                  <Pie data={statusDistributionData} options={pieChartOptions} />
                ) : (
                  <div
                    style={{ textAlign: 'center', marginTop: '2rem', color: '#6B7280' }}
                  >
                    Nenhum dado disponível
                  </div>
                )}
              </div>
            </ChartCard>

            <ChartCard>
              <ChartTitle
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                Eficiência por Produto
                <Popup
                  trigger={
                    <Icon
                      name="info circle"
                      style={{ color: '#6B7280', cursor: 'help' }}
                    />
                  }
                  content="Comparação da eficiência entre produtos: Seguro Incêndio (100% integrado), Seguro Fiança (parcialmente integrado) e Capitalização (100% manual)"
                  position="top center"
                  size="small"
                />
              </ChartTitle>
              <div style={{ height: '300px', padding: '1rem' }}>
                {chartData.productData.datasets &&
                chartData.productData.datasets.length > 0 ? (
                  <Bar data={chartData.productData} options={chartOptions} />
                ) : (
                  <div
                    style={{ textAlign: 'center', marginTop: '2rem', color: '#6B7280' }}
                  >
                    Nenhum dado disponível
                  </div>
                )}
              </div>
            </ChartCard>

            <ChartCard>
              <ChartTitle
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                Tendência do Período
                <Popup
                  trigger={
                    <Icon
                      name="info circle"
                      style={{ color: '#6B7280', cursor: 'help' }}
                    />
                  }
                  content="Evolução diária dos registros integrados, manuais e com fallback ao longo dos últimos 7 dias, permitindo identificar padrões e tendências"
                  position="top center"
                  size="small"
                />
              </ChartTitle>
              <div style={{ height: '300px', padding: '1rem' }}>
                {chartData.trendData.datasets &&
                chartData.trendData.datasets.length > 0 ? (
                  <Line data={chartData.trendData} options={chartOptions} />
                ) : (
                  <div
                    style={{ textAlign: 'center', marginTop: '2rem', color: '#6B7280' }}
                  >
                    Nenhum dado disponível
                  </div>
                )}
              </div>
            </ChartCard>
          </ChartGrid>
        </>
      )}
    </Container>
  );
};

export default connect(mapState, mapDispatch)(IntegrationsDashboard);
